import React, { useEffect, useState } from 'react';
import { Box, Grid, Typography, Card, CardContent, Select, MenuItem, FormControl, InputLabel, ToggleButton, ToggleButtonGroup, Button, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Chip, IconButton, useTheme, useMediaQuery } from '@mui/material';
import { ActivityIcon, BarChart3Icon, RefreshCcwIcon, CheckCircle2Icon, XCircleIcon, ClockIcon, DollarSignIcon, TimerIcon, UsersIcon, BuildingIcon, ShieldAlertIcon, ArrowRightLeftIcon, BellIcon, WalletIcon, TrendingUpIcon, AlertTriangleIcon, InfoIcon, CheckIcon, ExternalLinkIcon, GlobeIcon, LandmarkIcon, ZapIcon, BanknoteIcon } from 'lucide-react';
import { motion } from 'framer-motion';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip as RechartsTooltip, ResponsiveContainer, AreaChart, Area } from 'recharts';
import TopBar from '../components/TopBar';
import Sidebar from '../components/Sidebar';
import KPICard from '../components/KPICard';
import ChartCard from '../components/ChartCard';
interface DashboardProps {
  onLogout: () => void;
}
// ─── MOCK DATA ───────────────────────────────────────────────
const REGIONAL_BREAKDOWN = [{
  region: 'Ghana',
  flag: '🇬🇭',
  transactions: 1247,
  volume: '$3.2M',
  successRate: '96.1%',
  failed: 22,
  pending: 34
}, {
  region: 'Nigeria',
  flag: '🇳🇬',
  transactions: 1856,
  volume: '$4.8M',
  successRate: '94.8%',
  failed: 38,
  pending: 52
}, {
  region: 'Kenya',
  flag: '🇰🇪',
  transactions: 892,
  volume: '$2.1M',
  successRate: '95.7%',
  failed: 15,
  pending: 24
}, {
  region: 'Senegal',
  flag: '🇸🇳',
  transactions: 452,
  volume: '$1.2M',
  successRate: '96.5%',
  failed: 8,
  pending: 12
}, {
  region: 'Uganda',
  flag: '🇺🇬',
  transactions: 400,
  volume: '$1.1M',
  successRate: '95.0%',
  failed: 6,
  pending: 13
}];
const PARTNER_CARDS = [{
  title: 'Active MTOs',
  count: 15,
  color: '#0052CC',
  icon: <ArrowRightLeftIcon size={20} data-id="element-3478" />,
  top3: ['MoneyGram', 'Western Union', 'WorldRemit']
}, {
  title: 'Active PSPs / Fintechs',
  count: 8,
  color: '#0B7923',
  icon: <ZapIcon size={20} data-id="element-3479" />,
  top3: ['Flutterwave', 'M-Pesa', 'Orange Money']
}, {
  title: 'Active IMTOs',
  count: 7,
  color: '#6554C0',
  icon: <GlobeIcon size={20} data-id="element-3480" />,
  top3: ['Paga Ghana', 'MoneyGram Nigeria', 'WorldRemit Kenya']
}, {
  title: 'Active Banks',
  count: 12,
  color: '#0052CC',
  icon: <LandmarkIcon size={20} data-id="element-3481" />,
  top3: ['Ghana (4)', 'Nigeria (3)', 'Kenya (3)']
}, {
  title: 'Sub-Agents',
  count: 10,
  color: '#B38600',
  icon: <UsersIcon size={20} data-id="element-3482" />,
  top3: ['3 IMTO', '4 Fintech', '3 Cash']
}, {
  title: 'Total Partners',
  count: 52,
  color: '#1F2937',
  icon: <BuildingIcon size={20} data-id="element-3483" />,
  top3: ['Across 5 regions', '15 countries', '3 partner types']
}];
const COMPLIANCE_CASES = [{
  id: 'CMP-2025-0147',
  type: 'AML Alert',
  region: 'Ghana',
  risk: 'High',
  status: 'Under Review',
  age: '2h'
}, {
  id: 'CMP-2025-0146',
  type: 'KYC Expiry',
  region: 'Nigeria',
  risk: 'Medium',
  status: 'Pending',
  age: '4h'
}, {
  id: 'CMP-2025-0145',
  type: 'Sanctions Hit',
  region: 'Kenya',
  risk: 'Critical',
  status: 'Escalated',
  age: '1h'
}, {
  id: 'CMP-2025-0144',
  type: 'PEP Match',
  region: 'Senegal',
  risk: 'High',
  status: 'Under Review',
  age: '6h'
}, {
  id: 'CMP-2025-0143',
  type: 'Threshold Alert',
  region: 'Uganda',
  risk: 'Low',
  status: 'Pending',
  age: '8h'
}];
const TOP_CORRIDORS = [{
  corridor: 'Ghana → UK',
  volume: '$3.2M',
  txns: '1,247',
  avgTime: '1.8s',
  successRate: '96.1%'
}, {
  corridor: 'Nigeria → US',
  volume: '$2.8M',
  txns: '1,056',
  avgTime: '2.3s',
  successRate: '94.8%'
}, {
  corridor: 'Kenya → UK',
  volume: '$1.9M',
  txns: '742',
  avgTime: '1.5s',
  successRate: '97.2%'
}, {
  corridor: 'Senegal → France',
  volume: '$1.2M',
  txns: '452',
  avgTime: '2.1s',
  successRate: '96.5%'
}, {
  corridor: 'Ghana → Nigeria',
  volume: '$0.9M',
  txns: '389',
  avgTime: '1.2s',
  successRate: '98.1%'
}];
const FX_RATES = [{
  pair: 'USD/GHS',
  rate: '14.85',
  change: '+0.12',
  changeNum: 0.12,
  updated: '2m ago'
}, {
  pair: 'USD/NGN',
  rate: '1,580.50',
  change: '-2.30',
  changeNum: -2.3,
  updated: '1m ago'
}, {
  pair: 'USD/KES',
  rate: '129.45',
  change: '+0.35',
  changeNum: 0.35,
  updated: '3m ago'
}, {
  pair: 'USD/XOF',
  rate: '605.20',
  change: '-0.80',
  changeNum: -0.8,
  updated: '2m ago'
}, {
  pair: 'USD/UGX',
  rate: '3,745.00',
  change: '+5.00',
  changeNum: 5.0,
  updated: '4m ago'
}, {
  pair: 'GBP/GHS',
  rate: '18.92',
  change: '+0.15',
  changeNum: 0.15,
  updated: '2m ago'
}, {
  pair: 'EUR/XOF',
  rate: '655.96',
  change: 'fixed',
  changeNum: 0,
  updated: '—'
}];
const ALERTS = [{
  severity: 'critical',
  color: '#AE2A19',
  icon: '🔴',
  message: 'Virtual account GH-VA-001 balance below threshold ($2,450 / $10,000 min)',
  action: 'Fund Now',
  link: '/funding-settlement'
}, {
  severity: 'warning',
  color: '#B38600',
  icon: '🟡',
  message: '3 compliance cases pending >24 hours — escalation required',
  action: 'Review Cases',
  link: '/compliance-cases'
}, {
  severity: 'warning',
  color: '#B38600',
  icon: '🟡',
  message: 'Settlement batch NG-SET-2025-0147 delayed — expected 14:00 UTC',
  action: 'View Settlement',
  link: '/funding-settlement'
}, {
  severity: 'info',
  color: '#0052CC',
  icon: '🔵',
  message: 'KYB renewal due for Paga Ghana in 14 days',
  action: 'View Details',
  link: '/sub-agents'
}, {
  severity: 'success',
  color: '#0B7923',
  icon: '🟢',
  message: 'Monthly regulatory report auto-submitted to Ghana FIU',
  action: 'Dismiss',
  link: ''
}];
const CURRENCY_BALANCES = [{
  currency: 'GHS',
  flag: '🇬🇭',
  balance: '1,245,600',
  usdEquiv: '$83,878',
  partners: 8,
  updated: '2m ago'
}, {
  currency: 'NGN',
  flag: '🇳🇬',
  balance: '458,200,000',
  usdEquiv: '$289,873',
  partners: 12,
  updated: '1m ago'
}, {
  currency: 'KES',
  flag: '🇰🇪',
  balance: '42,500,000',
  usdEquiv: '$328,378',
  partners: 6,
  updated: '3m ago'
}, {
  currency: 'XOF',
  flag: '🇸🇳',
  balance: '175,000,000',
  usdEquiv: '$289,256',
  partners: 4,
  updated: '2m ago'
}, {
  currency: 'UGX',
  flag: '🇺🇬',
  balance: '1,250,000,000',
  usdEquiv: '$333,778',
  partners: 3,
  updated: '5m ago'
}, {
  currency: 'USD',
  flag: '🌐',
  balance: '2,450,000',
  usdEquiv: '$2,450,000',
  partners: 15,
  updated: '1m ago'
}];
const HOURLY_VOLUME_DATA = [{
  hour: '06:00',
  volume: 180,
  txns: 420
}, {
  hour: '08:00',
  volume: 340,
  txns: 780
}, {
  hour: '10:00',
  volume: 520,
  txns: 1100
}, {
  hour: '12:00',
  volume: 680,
  txns: 1340
}, {
  hour: '14:00',
  volume: 590,
  txns: 1180
}, {
  hour: '16:00',
  volume: 450,
  txns: 920
}, {
  hour: '18:00',
  volume: 320,
  txns: 640
}, {
  hour: '20:00',
  volume: 210,
  txns: 460
}];
// ─── HELPERS ─────────────────────────────────────────────────
const riskColor = (risk: string) => {
  switch (risk) {
    case 'Critical':
      return '#AE2A19';
    case 'High':
      return '#B38600';
    case 'Medium':
      return '#0052CC';
    case 'Low':
      return '#0B7923';
    default:
      return '#6B7280';
  }
};
const statusColor = (status: string) => {
  switch (status) {
    case 'Escalated':
      return '#AE2A19';
    case 'Under Review':
      return '#B38600';
    case 'Pending':
      return '#0052CC';
    case 'Resolved':
      return '#0B7923';
    default:
      return '#6B7280';
  }
};
const fadeUp = {
  hidden: {
    opacity: 0,
    y: 16
  },
  visible: (i: number) => ({
    opacity: 1,
    y: 0,
    transition: {
      delay: i * 0.06,
      duration: 0.35,
      ease: 'easeOut'
    }
  })
};
// ─── SECTION HEADER ──────────────────────────────────────────
function SectionHeader({
  title,
  icon,
  action
}: {
  title: string;
  icon: React.ReactNode;
  action?: React.ReactNode;
}) {
  return <Box sx={{
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    mb: 2,
    mt: 4
  }} data-id="element-3484">
      <Box sx={{
      display: 'flex',
      alignItems: 'center',
      gap: 1
    }} data-id="element-3485">
        <Box sx={{
        color: '#0052CC',
        display: 'flex'
      }} data-id="element-3486">
          {icon}
        </Box>
        <Typography variant="h6" sx={{
        fontWeight: 700,
        color: '#1F2937',
        fontSize: '1.05rem'
      }} data-id="element-3487">
          {title}
        </Typography>
      </Box>
      {action}
    </Box>;
}
// ─── TABLE HEADER CELL ───────────────────────────────────────
const thSx = {
  fontWeight: 700,
  fontSize: '0.7rem',
  color: '#0052CC',
  textTransform: 'uppercase' as const,
  letterSpacing: '0.04em',
  bgcolor: '#F3F5F7',
  borderBottom: '2px solid #E5E7EB',
  py: 1.2,
  px: 1.5,
  whiteSpace: 'nowrap' as const
};
const tdSx = {
  fontSize: '0.82rem',
  color: '#1F2937',
  py: 1,
  px: 1.5,
  borderBottom: '1px solid #F3F5F7'
};
// ─── MAIN COMPONENT ─────────────────────────────────────────
const Dashboard: React.FC<DashboardProps> = ({
  onLogout
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [mobileOpen, setMobileOpen] = useState(false);
  const [view, setView] = useState<'realtime' | 'performance'>('realtime');
  const [region, setRegion] = useState('all');
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [lastRefresh, setLastRefresh] = useState(new Date());
  const handleDrawerToggle = () => setMobileOpen(!mobileOpen);
  const handleViewChange = (_: React.MouseEvent<HTMLElement>, v: 'realtime' | 'performance' | null) => {
    if (v) {
      setView(v);
      refreshData();
    }
  };
  const refreshData = () => {
    setIsRefreshing(true);
    setTimeout(() => {
      setLastRefresh(new Date());
      setIsRefreshing(false);
    }, 600);
  };
  useEffect(() => {
    if (view === 'realtime') {
      const interval = setInterval(refreshData, 5 * 60 * 1000);
      return () => clearInterval(interval);
    }
  }, [view]);
  const filteredRegional = region === 'all' ? REGIONAL_BREAKDOWN : REGIONAL_BREAKDOWN.filter(r => {
    if (region === 'west') return ['Ghana', 'Nigeria', 'Senegal'].includes(r.region);
    if (region === 'east') return ['Kenya', 'Uganda'].includes(r.region);
    return true;
  });
  return <Box sx={{
    display: 'flex',
    minHeight: '100vh',
    bgcolor: '#F8F9FA'
  }} data-id="element-3488">
      <Sidebar mobileOpen={mobileOpen} handleDrawerToggle={handleDrawerToggle} onLogout={onLogout} data-id="element-3489" />
      <Box sx={{
      flexGrow: 1,
      ml: 0,
      width: {
        sm: 'calc(100% - 240px)'
      }
    }} data-id="element-3490">
        <TopBar handleDrawerToggle={handleDrawerToggle} data-id="element-3491" />
        <Box component="main" sx={{
        mt: 8,
        p: {
          xs: 2,
          sm: 3
        },
        maxWidth: 1400,
        mx: 'auto'
      }} data-id="element-3492">
          {/* ═══ HEADER ═══ */}
          <motion.div initial="hidden" animate="visible" variants={fadeUp} custom={0} data-id="element-3493">
            <Box sx={{
            display: 'flex',
            flexWrap: 'wrap',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 2,
            mb: 3
          }} data-id="element-3494">
              <Box data-id="element-3495">
                <Typography variant="h5" sx={{
                fontWeight: 800,
                color: '#1F2937',
                letterSpacing: '-0.02em'
              }} data-id="element-3496">
                  Ecobank Operations Dashboard
                </Typography>
                <Typography variant="body2" sx={{
                color: '#6B7280',
                mt: 0.3
              }} data-id="element-3497">
                  Regional remittance operations overview
                </Typography>
              </Box>
              <Box sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1.5,
              flexWrap: 'wrap'
            }} data-id="element-3498">
                <FormControl size="small" sx={{
                minWidth: 150
              }} data-id="element-3499">
                  <InputLabel data-id="element-3500">Region</InputLabel>
                  <Select value={region} label="Region" onChange={e => setRegion(e.target.value)} data-id="element-3501">
                    <MenuItem value="all" data-id="element-3502">All Regions</MenuItem>
                    <MenuItem value="west" data-id="element-3503">West Africa</MenuItem>
                    <MenuItem value="east" data-id="element-3504">East Africa</MenuItem>
                    <MenuItem value="central" data-id="element-3505">Central Africa</MenuItem>
                  </Select>
                </FormControl>
                <ToggleButtonGroup value={view} exclusive onChange={handleViewChange} size="small" sx={{
                height: 36
              }} data-id="element-3506">
                  <ToggleButton value="realtime" sx={{
                  textTransform: 'none',
                  fontSize: '0.8rem',
                  px: 2
                }} data-id="element-3507">
                    <ActivityIcon size={14} style={{
                    marginRight: 6
                  }} data-id="element-3508" />{' '}
                    Real-time
                  </ToggleButton>
                  <ToggleButton value="performance" sx={{
                  textTransform: 'none',
                  fontSize: '0.8rem',
                  px: 2
                }} data-id="element-3509">
                    <BarChart3Icon size={14} style={{
                    marginRight: 6
                  }} data-id="element-3510" />{' '}
                    Performance
                  </ToggleButton>
                </ToggleButtonGroup>
                <Button size="small" variant="outlined" startIcon={<RefreshCcwIcon size={14} className={isRefreshing ? 'animate-spin' : ''} data-id="element-3512" />} onClick={refreshData} sx={{
                textTransform: 'none',
                fontSize: '0.78rem',
                borderColor: '#E5E7EB',
                color: '#6B7280'
              }} data-id="element-3511">
                  Refresh
                </Button>
                <Typography variant="caption" sx={{
                color: '#9CA3AF',
                whiteSpace: 'nowrap'
              }} data-id="element-3513">
                  Last:{' '}
                  {lastRefresh.toLocaleTimeString('en-US', {
                  hour: '2-digit',
                  minute: '2-digit',
                  second: '2-digit'
                })}
                </Typography>
              </Box>
            </Box>
          </motion.div>

          {/* ═══ SECTION 1: TRANSACTION METRICS KPIs ═══ */}
          <motion.div initial="hidden" animate="visible" variants={fadeUp} custom={1} data-id="element-3514">
            <Grid container spacing={2} sx={{
            mb: 3
          }} data-id="element-3515">
              {[{
              title: 'Total Transactions Today',
              value: '4,847',
              color: '#0052CC',
              icon: <ActivityIcon size={18} data-id="element-3516" />,
              trend: {
                value: 12.5,
                label: 'vs yesterday',
                data: [3200, 3400, 3800, 4100, 4300, 4500, 4600, 4700, 4847]
              }
            }, {
              title: 'Successful',
              value: '4,623 (95.4%)',
              color: '#0B7923',
              icon: <CheckCircle2Icon size={18} data-id="element-3517" />,
              trend: {
                value: 3.2,
                label: 'vs yesterday',
                data: [3000, 3200, 3500, 3800, 4000, 4200, 4400, 4500, 4623]
              }
            }, {
              title: 'Failed',
              value: '89 (1.8%)',
              color: '#AE2A19',
              icon: <XCircleIcon size={18} data-id="element-3518" />,
              trendColorMode: 'pending' as const,
              trend: {
                value: -5.3,
                label: 'vs yesterday',
                data: [120, 110, 105, 98, 95, 92, 90, 89, 89]
              }
            }, {
              title: 'Pending',
              value: '135 (2.8%)',
              color: '#B38600',
              icon: <ClockIcon size={18} data-id="element-3519" />,
              trendColorMode: 'pending' as const,
              trend: {
                value: 1.5,
                label: 'vs yesterday',
                data: [100, 110, 120, 125, 130, 128, 132, 134, 135]
              }
            }, {
              title: 'Total Volume',
              value: '$12.4M',
              color: '#0052CC',
              icon: <DollarSignIcon size={18} data-id="element-3520" />,
              trend: {
                value: 8.7,
                label: 'vs yesterday',
                data: [8.2, 9.1, 9.8, 10.5, 11.0, 11.4, 11.8, 12.1, 12.4]
              }
            }, {
              title: 'Avg Processing Time',
              value: '2.1s',
              color: '#6554C0',
              icon: <TimerIcon size={18} data-id="element-3521" />,
              trend: {
                value: -12.5,
                label: 'vs yesterday',
                data: [3.2, 2.9, 2.7, 2.5, 2.3, 2.2, 2.1, 2.1, 2.1]
              }
            }].map((kpi, i) => <Grid item xs={6} sm={4} md={2} key={kpi.title} data-id="element-3522">
                  <KPICard {...kpi} data-id="element-3523" />
                </Grid>)}
            </Grid>
          </motion.div>

          {/* Regional Breakdown Table */}
          <motion.div initial="hidden" animate="visible" variants={fadeUp} custom={2} data-id="element-3524">
            <Card sx={{
            mb: 3,
            borderRadius: '8px',
            border: '1px solid #E5E7EB',
            boxShadow: '0 1px 2px rgba(0,0,0,0.05)'
          }} data-id="element-3525">
              <CardContent sx={{
              p: 0
            }} data-id="element-3526">
                <Box sx={{
                px: 2.5,
                py: 1.5,
                borderBottom: '1px solid #F3F5F7',
                display: 'flex',
                alignItems: 'center',
                gap: 1
              }} data-id="element-3527">
                  <GlobeIcon size={16} color="#0052CC" data-id="element-3528" />
                  <Typography variant="subtitle2" sx={{
                  fontWeight: 700,
                  color: '#1F2937'
                }} data-id="element-3529">
                    Regional Breakdown
                  </Typography>
                </Box>
                <TableContainer data-id="element-3530">
                  <Table size="small" data-id="element-3531">
                    <TableHead data-id="element-3532">
                      <TableRow data-id="element-3533">
                        <TableCell sx={thSx} data-id="element-3534">Region</TableCell>
                        <TableCell sx={thSx} align="right" data-id="element-3535">
                          Transactions
                        </TableCell>
                        <TableCell sx={thSx} align="right" data-id="element-3536">
                          Volume
                        </TableCell>
                        <TableCell sx={thSx} align="right" data-id="element-3537">
                          Success Rate
                        </TableCell>
                        <TableCell sx={thSx} align="right" data-id="element-3538">
                          Failed
                        </TableCell>
                        <TableCell sx={thSx} align="right" data-id="element-3539">
                          Pending
                        </TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody data-id="element-3540">
                      {filteredRegional.map(row => <TableRow key={row.region} hover sx={{
                      '&:hover': {
                        bgcolor: '#FAFBFC'
                      }
                    }} data-id="element-3541">
                          <TableCell sx={tdSx} data-id="element-3542">
                            <Box sx={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 1
                        }} data-id="element-3543">
                              <span data-id="element-3544">{row.flag}</span>
                              <Typography variant="body2" sx={{
                            fontWeight: 600
                          }} data-id="element-3545">
                                {row.region}
                              </Typography>
                            </Box>
                          </TableCell>
                          <TableCell sx={tdSx} align="right" data-id="element-3546">
                            {row.transactions.toLocaleString()}
                          </TableCell>
                          <TableCell sx={tdSx} align="right" data-id="element-3547">
                            <strong data-id="element-3548">{row.volume}</strong>
                          </TableCell>
                          <TableCell sx={tdSx} align="right" data-id="element-3549">
                            <Chip label={row.successRate} size="small" sx={{
                          bgcolor: '#0B79231A',
                          color: '#0B7923',
                          fontWeight: 700,
                          fontSize: '0.72rem',
                          height: 22
                        }} data-id="element-3550" />
                          </TableCell>
                          <TableCell sx={{
                        ...tdSx,
                        color: '#AE2A19',
                        fontWeight: 600
                      }} align="right" data-id="element-3551">
                            {row.failed}
                          </TableCell>
                          <TableCell sx={{
                        ...tdSx,
                        color: '#B38600',
                        fontWeight: 600
                      }} align="right" data-id="element-3552">
                            {row.pending}
                          </TableCell>
                        </TableRow>)}
                    </TableBody>
                  </Table>
                </TableContainer>
              </CardContent>
            </Card>
          </motion.div>

          {/* Hourly Volume Chart */}
          <motion.div initial="hidden" animate="visible" variants={fadeUp} custom={3} data-id="element-3553">
            <Card sx={{
            mb: 3,
            borderRadius: '8px',
            border: '1px solid #E5E7EB',
            boxShadow: '0 1px 2px rgba(0,0,0,0.05)'
          }} data-id="element-3554">
              <CardContent sx={{
              p: 2.5
            }} data-id="element-3555">
                <Typography variant="subtitle2" sx={{
                fontWeight: 700,
                color: '#1F2937',
                mb: 2
              }} data-id="element-3556">
                  Today's Transaction Volume (Hourly)
                </Typography>
                <Box sx={{
                height: 220,
                width: '100%'
              }} data-id="element-3557">
                  <ResponsiveContainer width="100%" height="100%" data-id="element-3558">
                    <AreaChart data={HOURLY_VOLUME_DATA} margin={{
                    top: 5,
                    right: 20,
                    left: 0,
                    bottom: 0
                  }} data-id="element-3559">
                      <defs data-id="element-3560">
                        <linearGradient id="volumeGrad" x1="0" y1="0" x2="0" y2="1" data-id="element-3561">
                          <stop offset="5%" stopColor="#0052CC" stopOpacity={0.15} data-id="element-3562" />
                          <stop offset="95%" stopColor="#0052CC" stopOpacity={0.02} data-id="element-3563" />
                        </linearGradient>
                      </defs>
                      <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#E5E7EB" data-id="element-3564" />
                      <XAxis dataKey="hour" axisLine={false} tickLine={false} tick={{
                      fontSize: 11,
                      fill: '#6B7280'
                    }} data-id="element-3565" />
                      <YAxis axisLine={false} tickLine={false} tick={{
                      fontSize: 11,
                      fill: '#6B7280'
                    }} data-id="element-3566" />
                      <RechartsTooltip contentStyle={{
                      borderRadius: 8,
                      border: '1px solid #E5E7EB',
                      boxShadow: '0 4px 12px rgba(0,0,0,0.08)'
                    }} formatter={(value: any, name: string) => [name === 'volume' ? `$${value}K` : value, name === 'volume' ? 'Volume' : 'Transactions']} data-id="element-3567" />
                      <Area type="monotone" dataKey="volume" stroke="#0052CC" strokeWidth={2.5} fill="url(#volumeGrad)" data-id="element-3568" />
                      <Area type="monotone" dataKey="txns" stroke="#0B7923" strokeWidth={1.5} fill="none" strokeDasharray="4 4" data-id="element-3569" />
                    </AreaChart>
                  </ResponsiveContainer>
                </Box>
              </CardContent>
            </Card>
          </motion.div>

          {/* ═══ SECTION 2: PARTNER & BANK OVERVIEW ═══ */}
          <SectionHeader title="Partner & Bank Overview" icon={<BuildingIcon size={18} data-id="element-3571" />} data-id="element-3570" />
          <motion.div initial="hidden" animate="visible" variants={fadeUp} custom={4} data-id="element-3572">
            <Grid container spacing={2} sx={{
            mb: 3
          }} data-id="element-3573">
              {PARTNER_CARDS.map((card, i) => <Grid item xs={12} sm={6} md={4} key={card.title} data-id="element-3574">
                  <motion.div variants={fadeUp} custom={4 + i * 0.5} data-id="element-3575">
                    <Card sx={{
                  borderRadius: '8px',
                  border: '1px solid #E5E7EB',
                  boxShadow: '0 1px 2px rgba(0,0,0,0.05)',
                  height: '100%',
                  transition: 'box-shadow 200ms',
                  '&:hover': {
                    boxShadow: '0 4px 12px rgba(0,0,0,0.08)'
                  }
                }} data-id="element-3576">
                      <CardContent sx={{
                    p: 2.5
                  }} data-id="element-3577">
                        <Box sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      mb: 1.5
                    }} data-id="element-3578">
                          <Box sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1
                      }} data-id="element-3579">
                            <Box sx={{
                          width: 32,
                          height: 32,
                          borderRadius: '6px',
                          bgcolor: `${card.color}1A`,
                          color: card.color,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center'
                        }} data-id="element-3580">
                              {card.icon}
                            </Box>
                            <Typography variant="body2" sx={{
                          fontWeight: 600,
                          color: '#6B7280',
                          fontSize: '0.78rem',
                          textTransform: 'uppercase',
                          letterSpacing: '0.02em'
                        }} data-id="element-3581">
                              {card.title}
                            </Typography>
                          </Box>
                          <Typography variant="h5" sx={{
                        fontWeight: 800,
                        color: card.color
                      }} data-id="element-3582">
                            {card.count}
                          </Typography>
                        </Box>
                        <Box sx={{
                      display: 'flex',
                      flexWrap: 'wrap',
                      gap: 0.5
                    }} data-id="element-3583">
                          {card.top3.map(item => <Chip key={item} label={item} size="small" sx={{
                        bgcolor: '#F3F5F7',
                        color: '#1F2937',
                        fontSize: '0.7rem',
                        height: 22,
                        fontWeight: 500
                      }} data-id="element-3584" />)}
                        </Box>
                      </CardContent>
                    </Card>
                  </motion.div>
                </Grid>)}
            </Grid>
          </motion.div>

          {/* ═══ SECTION 3: COMPLIANCE CASE STATUS ═══ */}
          <SectionHeader title="Compliance Case Status" icon={<ShieldAlertIcon size={18} data-id="element-3586" />} action={<Button size="small" endIcon={<ExternalLinkIcon size={14} data-id="element-3588" />} href="/compliance-cases" sx={{
          textTransform: 'none',
          fontSize: '0.78rem',
          color: '#0052CC'
        }} data-id="element-3587">
                View All Cases
              </Button>} data-id="element-3585" />
          <motion.div initial="hidden" animate="visible" variants={fadeUp} custom={6} data-id="element-3589">
            <Card sx={{
            mb: 3,
            borderRadius: '8px',
            border: '1px solid #E5E7EB',
            boxShadow: '0 1px 2px rgba(0,0,0,0.05)'
          }} data-id="element-3590">
              <CardContent sx={{
              p: 0
            }} data-id="element-3591">
                {/* Compliance KPI row */}
                <Box sx={{
                display: 'flex',
                flexWrap: 'wrap',
                gap: 2,
                p: 2.5,
                borderBottom: '1px solid #F3F5F7'
              }} data-id="element-3592">
                  {[{
                  label: 'Active Cases',
                  value: 12,
                  color: '#B38600'
                }, {
                  label: 'Pending Approvals',
                  value: 5,
                  color: '#AE2A19'
                }, {
                  label: 'Resolved Today',
                  value: 8,
                  color: '#0B7923'
                }, {
                  label: 'Escalated',
                  value: 2,
                  color: '#AE2A19'
                }].map(kpi => <Box key={kpi.label} sx={{
                  flex: '1 1 120px',
                  textAlign: 'center',
                  py: 1
                }} data-id="element-3593">
                      <Typography variant="h5" sx={{
                    fontWeight: 800,
                    color: kpi.color
                  }} data-id="element-3594">
                        {kpi.value}
                      </Typography>
                      <Typography variant="caption" sx={{
                    color: '#6B7280',
                    fontWeight: 600,
                    textTransform: 'uppercase',
                    letterSpacing: '0.03em',
                    fontSize: '0.65rem'
                  }} data-id="element-3595">
                        {kpi.label}
                      </Typography>
                    </Box>)}
                </Box>
                {/* Compliance mini table */}
                <TableContainer data-id="element-3596">
                  <Table size="small" data-id="element-3597">
                    <TableHead data-id="element-3598">
                      <TableRow data-id="element-3599">
                        <TableCell sx={thSx} data-id="element-3600">Case ID</TableCell>
                        <TableCell sx={thSx} data-id="element-3601">Type</TableCell>
                        <TableCell sx={thSx} data-id="element-3602">Region</TableCell>
                        <TableCell sx={thSx} data-id="element-3603">Risk Level</TableCell>
                        <TableCell sx={thSx} data-id="element-3604">Status</TableCell>
                        <TableCell sx={thSx} align="right" data-id="element-3605">
                          Age
                        </TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody data-id="element-3606">
                      {COMPLIANCE_CASES.map(c => <TableRow key={c.id} hover sx={{
                      '&:hover': {
                        bgcolor: '#FAFBFC'
                      }
                    }} data-id="element-3607">
                          <TableCell sx={{
                        ...tdSx,
                        fontWeight: 600,
                        color: '#0052CC',
                        fontFamily: 'monospace',
                        fontSize: '0.78rem'
                      }} data-id="element-3608">
                            {c.id}
                          </TableCell>
                          <TableCell sx={tdSx} data-id="element-3609">{c.type}</TableCell>
                          <TableCell sx={tdSx} data-id="element-3610">{c.region}</TableCell>
                          <TableCell sx={tdSx} data-id="element-3611">
                            <Chip label={c.risk} size="small" sx={{
                          bgcolor: `${riskColor(c.risk)}1A`,
                          color: riskColor(c.risk),
                          fontWeight: 700,
                          fontSize: '0.68rem',
                          height: 22
                        }} data-id="element-3612" />
                          </TableCell>
                          <TableCell sx={tdSx} data-id="element-3613">
                            <Chip label={c.status} size="small" variant="outlined" sx={{
                          borderColor: statusColor(c.status),
                          color: statusColor(c.status),
                          fontWeight: 600,
                          fontSize: '0.68rem',
                          height: 22
                        }} data-id="element-3614" />
                          </TableCell>
                          <TableCell sx={{
                        ...tdSx,
                        color: '#6B7280',
                        fontWeight: 600
                      }} align="right" data-id="element-3615">
                            {c.age}
                          </TableCell>
                        </TableRow>)}
                    </TableBody>
                  </Table>
                </TableContainer>
              </CardContent>
            </Card>
          </motion.div>

          {/* ═══ SECTION 4: CORRIDOR & FX TRACKING ═══ */}
          <SectionHeader title="Corridor & FX Tracking" icon={<ArrowRightLeftIcon size={18} data-id="element-3617" />} data-id="element-3616" />
          <motion.div initial="hidden" animate="visible" variants={fadeUp} custom={7} data-id="element-3618">
            <Grid container spacing={2} sx={{
            mb: 3
          }} data-id="element-3619">
              {/* Top Corridors */}
              <Grid item xs={12} md={7} data-id="element-3620">
                <Card sx={{
                borderRadius: '8px',
                border: '1px solid #E5E7EB',
                boxShadow: '0 1px 2px rgba(0,0,0,0.05)',
                height: '100%'
              }} data-id="element-3621">
                  <CardContent sx={{
                  p: 0
                }} data-id="element-3622">
                    <Box sx={{
                    px: 2.5,
                    py: 1.5,
                    borderBottom: '1px solid #F3F5F7'
                  }} data-id="element-3623">
                      <Typography variant="subtitle2" sx={{
                      fontWeight: 700,
                      color: '#1F2937'
                    }} data-id="element-3624">
                        Top Corridors
                      </Typography>
                    </Box>
                    <TableContainer data-id="element-3625">
                      <Table size="small" data-id="element-3626">
                        <TableHead data-id="element-3627">
                          <TableRow data-id="element-3628">
                            <TableCell sx={thSx} data-id="element-3629">Corridor</TableCell>
                            <TableCell sx={thSx} align="right" data-id="element-3630">
                              Volume
                            </TableCell>
                            <TableCell sx={thSx} align="right" data-id="element-3631">
                              Txns
                            </TableCell>
                            <TableCell sx={thSx} align="right" data-id="element-3632">
                              Avg Time
                            </TableCell>
                            <TableCell sx={thSx} align="right" data-id="element-3633">
                              Success
                            </TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody data-id="element-3634">
                          {TOP_CORRIDORS.map(row => <TableRow key={row.corridor} hover sx={{
                          '&:hover': {
                            bgcolor: '#FAFBFC'
                          }
                        }} data-id="element-3635">
                              <TableCell sx={{
                            ...tdSx,
                            fontWeight: 600
                          }} data-id="element-3636">
                                {row.corridor}
                              </TableCell>
                              <TableCell sx={{
                            ...tdSx,
                            fontWeight: 700
                          }} align="right" data-id="element-3637">
                                {row.volume}
                              </TableCell>
                              <TableCell sx={tdSx} align="right" data-id="element-3638">
                                {row.txns}
                              </TableCell>
                              <TableCell sx={tdSx} align="right" data-id="element-3639">
                                {row.avgTime}
                              </TableCell>
                              <TableCell sx={tdSx} align="right" data-id="element-3640">
                                <Chip label={row.successRate} size="small" sx={{
                              bgcolor: '#0B79231A',
                              color: '#0B7923',
                              fontWeight: 700,
                              fontSize: '0.7rem',
                              height: 22
                            }} data-id="element-3641" />
                              </TableCell>
                            </TableRow>)}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </CardContent>
                </Card>
              </Grid>

              {/* Live FX Rates */}
              <Grid item xs={12} md={5} data-id="element-3642">
                <Card sx={{
                borderRadius: '8px',
                border: '1px solid #E5E7EB',
                boxShadow: '0 1px 2px rgba(0,0,0,0.05)',
                height: '100%'
              }} data-id="element-3643">
                  <CardContent sx={{
                  p: 0
                }} data-id="element-3644">
                    <Box sx={{
                    px: 2.5,
                    py: 1.5,
                    borderBottom: '1px solid #F3F5F7',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between'
                  }} data-id="element-3645">
                      <Typography variant="subtitle2" sx={{
                      fontWeight: 700,
                      color: '#1F2937'
                    }} data-id="element-3646">
                        Live FX Rates
                      </Typography>
                      <Chip label="LIVE" size="small" sx={{
                      bgcolor: '#0B79231A',
                      color: '#0B7923',
                      fontWeight: 700,
                      fontSize: '0.6rem',
                      height: 20,
                      animation: 'pulse 2s infinite'
                    }} data-id="element-3647" />
                    </Box>
                    <TableContainer data-id="element-3648">
                      <Table size="small" data-id="element-3649">
                        <TableHead data-id="element-3650">
                          <TableRow data-id="element-3651">
                            <TableCell sx={thSx} data-id="element-3652">Pair</TableCell>
                            <TableCell sx={thSx} align="right" data-id="element-3653">
                              Rate
                            </TableCell>
                            <TableCell sx={thSx} align="right" data-id="element-3654">
                              Change
                            </TableCell>
                            <TableCell sx={thSx} align="right" data-id="element-3655">
                              Updated
                            </TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody data-id="element-3656">
                          {FX_RATES.map(fx => <TableRow key={fx.pair} hover sx={{
                          '&:hover': {
                            bgcolor: '#FAFBFC'
                          }
                        }} data-id="element-3657">
                              <TableCell sx={{
                            ...tdSx,
                            fontWeight: 700,
                            fontFamily: 'monospace',
                            fontSize: '0.78rem'
                          }} data-id="element-3658">
                                {fx.pair}
                              </TableCell>
                              <TableCell sx={{
                            ...tdSx,
                            fontWeight: 600
                          }} align="right" data-id="element-3659">
                                {fx.rate}
                              </TableCell>
                              <TableCell align="right" sx={{
                            ...tdSx,
                            fontWeight: 700,
                            color: fx.changeNum > 0 ? '#0B7923' : fx.changeNum < 0 ? '#AE2A19' : '#6B7280'
                          }} data-id="element-3660">
                                {fx.change === 'fixed' ? <Chip label="FIXED" size="small" sx={{
                              height: 18,
                              fontSize: '0.6rem',
                              bgcolor: '#F3F5F7',
                              color: '#6B7280'
                            }} data-id="element-3661" /> : fx.change}
                              </TableCell>
                              <TableCell sx={{
                            ...tdSx,
                            color: '#9CA3AF',
                            fontSize: '0.72rem'
                          }} align="right" data-id="element-3662">
                                {fx.updated}
                              </TableCell>
                            </TableRow>)}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
          </motion.div>

          {/* ═══ SECTION 5: ACTIONABLE ALERTS ═══ */}
          <SectionHeader title="Actionable Alerts & Notifications" icon={<BellIcon size={18} data-id="element-3664" />} data-id="element-3663" />
          <motion.div initial="hidden" animate="visible" variants={fadeUp} custom={8} data-id="element-3665">
            <Box sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: 1.5,
            mb: 3
          }} data-id="element-3666">
              {ALERTS.map((alert, i) => <motion.div key={i} variants={fadeUp} custom={8 + i * 0.3} data-id="element-3667">
                  <Card sx={{
                borderRadius: '8px',
                border: '1px solid #E5E7EB',
                borderLeft: `4px solid ${alert.color}`,
                boxShadow: '0 1px 2px rgba(0,0,0,0.04)',
                transition: 'box-shadow 200ms',
                '&:hover': {
                  boxShadow: '0 2px 8px rgba(0,0,0,0.06)'
                }
              }} data-id="element-3668">
                    <CardContent sx={{
                  py: 1.5,
                  px: 2.5,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  gap: 2,
                  '&:last-child': {
                    pb: 1.5
                  }
                }} data-id="element-3669">
                      <Box sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1.5,
                    flex: 1,
                    minWidth: 0
                  }} data-id="element-3670">
                        <Typography sx={{
                      fontSize: '1.1rem',
                      lineHeight: 1
                    }} data-id="element-3671">
                          {alert.icon}
                        </Typography>
                        <Typography variant="body2" sx={{
                      color: '#1F2937',
                      fontSize: '0.82rem'
                    }} data-id="element-3672">
                          {alert.message}
                        </Typography>
                      </Box>
                      <Button size="small" variant={alert.severity === 'critical' ? 'contained' : 'outlined'} href={alert.link || undefined} sx={{
                    textTransform: 'none',
                    fontSize: '0.72rem',
                    fontWeight: 600,
                    whiteSpace: 'nowrap',
                    ...(alert.severity === 'critical' ? {
                      bgcolor: alert.color,
                      '&:hover': {
                        bgcolor: alert.color,
                        opacity: 0.9
                      }
                    } : {
                      borderColor: alert.color,
                      color: alert.color
                    })
                  }} data-id="element-3673">
                        {alert.action}
                      </Button>
                    </CardContent>
                  </Card>
                </motion.div>)}
            </Box>
          </motion.div>

          {/* ═══ SECTION 6: MULTI-CURRENCY BALANCES ═══ */}
          <SectionHeader title="Multi-Currency Balances" icon={<WalletIcon size={18} data-id="element-3675" />} data-id="element-3674" />
          <motion.div initial="hidden" animate="visible" variants={fadeUp} custom={10} data-id="element-3676">
            <Card sx={{
            mb: 3,
            borderRadius: '8px',
            border: '1px solid #E5E7EB',
            boxShadow: '0 1px 2px rgba(0,0,0,0.05)'
          }} data-id="element-3677">
              <CardContent sx={{
              p: 0
            }} data-id="element-3678">
                <TableContainer data-id="element-3679">
                  <Table size="small" data-id="element-3680">
                    <TableHead data-id="element-3681">
                      <TableRow data-id="element-3682">
                        <TableCell sx={thSx} data-id="element-3683">Currency</TableCell>
                        <TableCell sx={thSx} align="right" data-id="element-3684">
                          Balance
                        </TableCell>
                        <TableCell sx={thSx} align="right" data-id="element-3685">
                          USD Equivalent
                        </TableCell>
                        <TableCell sx={thSx} align="right" data-id="element-3686">
                          Partners
                        </TableCell>
                        <TableCell sx={thSx} align="right" data-id="element-3687">
                          Last Updated
                        </TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody data-id="element-3688">
                      {CURRENCY_BALANCES.map(row => <TableRow key={row.currency} hover sx={{
                      '&:hover': {
                        bgcolor: '#FAFBFC'
                      }
                    }} data-id="element-3689">
                          <TableCell sx={tdSx} data-id="element-3690">
                            <Box sx={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 1
                        }} data-id="element-3691">
                              <span data-id="element-3692">{row.flag}</span>
                              <Typography variant="body2" sx={{
                            fontWeight: 700,
                            fontFamily: 'monospace'
                          }} data-id="element-3693">
                                {row.currency}
                              </Typography>
                            </Box>
                          </TableCell>
                          <TableCell sx={{
                        ...tdSx,
                        fontWeight: 600
                      }} align="right" data-id="element-3694">
                            {row.balance}
                          </TableCell>
                          <TableCell sx={{
                        ...tdSx,
                        fontWeight: 700,
                        color: '#0052CC'
                      }} align="right" data-id="element-3695">
                            {row.usdEquiv}
                          </TableCell>
                          <TableCell sx={tdSx} align="right" data-id="element-3696">
                            {row.partners}
                          </TableCell>
                          <TableCell sx={{
                        ...tdSx,
                        color: '#9CA3AF',
                        fontSize: '0.72rem'
                      }} align="right" data-id="element-3697">
                            {row.updated}
                          </TableCell>
                        </TableRow>)}
                    </TableBody>
                  </Table>
                </TableContainer>
              </CardContent>
            </Card>
          </motion.div>

          {/* ═══ SYSTEM STATUS BAR ═══ */}
          <motion.div initial="hidden" animate="visible" variants={fadeUp} custom={11} data-id="element-3698">
            <Card sx={{
            borderRadius: '8px',
            bgcolor: '#1F2937',
            border: 'none',
            mb: 2
          }} data-id="element-3699">
              <CardContent sx={{
              py: 1.5,
              px: 2.5,
              display: 'flex',
              flexWrap: 'wrap',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: 2,
              '&:last-child': {
                pb: 1.5
              }
            }} data-id="element-3700">
                <Box sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 2
              }} data-id="element-3701">
                  <Box sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 0.8
                }} data-id="element-3702">
                    <Box sx={{
                    width: 8,
                    height: 8,
                    borderRadius: '50%',
                    bgcolor: '#0B7923',
                    animation: 'pulse 2s infinite'
                  }} data-id="element-3703" />
                    <Typography variant="caption" sx={{
                    color: '#D1D5DB',
                    fontWeight: 600
                  }} data-id="element-3704">
                      All Systems Operational
                    </Typography>
                  </Box>
                  <Typography variant="caption" sx={{
                  color: '#6B7280'
                }} data-id="element-3705">
                    |
                  </Typography>
                  <Typography variant="caption" sx={{
                  color: '#9CA3AF'
                }} data-id="element-3706">
                    API Latency: 45ms
                  </Typography>
                  <Typography variant="caption" sx={{
                  color: '#6B7280'
                }} data-id="element-3707">
                    |
                  </Typography>
                  <Typography variant="caption" sx={{
                  color: '#9CA3AF'
                }} data-id="element-3708">
                    Uptime: 99.98%
                  </Typography>
                </Box>
                <Box sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 2
              }} data-id="element-3709">
                  <Typography variant="caption" sx={{
                  color: '#9CA3AF'
                }} data-id="element-3710">
                    Core Banking:{' '}
                    <span style={{
                    color: '#0B7923',
                    fontWeight: 700
                  }} data-id="element-3711">
                      Connected
                    </span>
                  </Typography>
                  <Typography variant="caption" sx={{
                  color: '#9CA3AF'
                }} data-id="element-3712">
                    SWIFT:{' '}
                    <span style={{
                    color: '#0B7923',
                    fontWeight: 700
                  }} data-id="element-3713">
                      Active
                    </span>
                  </Typography>
                  <Typography variant="caption" sx={{
                  color: '#9CA3AF'
                }} data-id="element-3714">
                    RTGS:{' '}
                    <span style={{
                    color: '#0B7923',
                    fontWeight: 700
                  }} data-id="element-3715">
                      Online
                    </span>
                  </Typography>
                </Box>
              </CardContent>
            </Card>
          </motion.div>
        </Box>
      </Box>

      {/* Pulse animation keyframes */}
      <style data-id="element-3716">{`
        @keyframes pulse {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.5; }
        }
        .animate-spin {
          animation: spin 1s linear infinite;
        }
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
      `}</style>
    </Box>;
};
export default Dashboard;